import { useState } from "react";
import { type MessageProps } from "~/common/components/Messages/Message";
import { registerUser } from "~/features/registration/api/registerUserForFree";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { trackRegisterForFree } from "~/app/analytics/ga/ga";
import { type FieldError, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { styled } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import { useSelector } from "react-redux";
import { selectSavedAffiliate, selectSavedSubsidiaryId } from "~/domains/affiliate/redux/selectors";
import {
  getAffiliateDataFromRegistrationLink,
  toRegistrationAffiliateContext,
} from "~/domains/affiliate/utils/affiliateHelper";

interface FormFields {
  email: string;
  generalTerms: boolean;
  privacyPolicy: boolean;
  marketingConsent: boolean;
  isRestrictedDomainLimitExceeded?: boolean;
}

interface UseRegisterForFreeOptions {
  onRegistrationSuccess?: () => void;
}

export const useRegisterForFree = (options?: UseRegisterForFreeOptions) => {
  const savedAffiliate = useSelector(selectSavedAffiliate);
  const savedSubsidiaryId = useSelector(selectSavedSubsidiaryId);
  const registrationAffiliateContext = toRegistrationAffiliateContext({
    registrationLink: getAffiliateDataFromRegistrationLink(),
    savedAffiliate,
    savedSubsidiaryId,
  });

  const {
    register,
    setError,
    formState: { errors },
    watch,
    setValue,
    control,
    handleSubmit,
  } = useForm<FormFields>({
    defaultValues: {
      email: "",
      generalTerms: false,
      privacyPolicy: false,
      marketingConsent: false,
    },
  });

  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);
  const {
    val: emailConfirmation,
    setTrue: showEmailConfirmation,
    setFalse: hideEmailConfirmation,
  } = useBooleanState(false);

  const [restrictedDomainAttemptsCount, setRestrictedDomainAttemptsCount] = useState(1);

  const setSubmissionError = (errorString: string) => {
    setError("email", { message: errorString });
    if (errorString === "error.restrictedDomain") {
      setRestrictedDomainAttemptsCount((count: number) => count + 1);
    }
  };

  const onSubmitHandler = handleSubmit(async (values) => {
    trackRegisterForFree("Register for Free");

    if (restrictedDomainAttemptsCount >= 3) {
      values.isRestrictedDomainLimitExceeded = true;
    }
    const response = await registerUser({ ...values, ...registrationAffiliateContext });
    if (response.error) {
      setSubmissionError(response.error);
    } else {
      showEmailConfirmation();
      options?.onRegistrationSuccess?.();
    }
  });

  const onResetClick = (email: string) => {
    setValue("email", email);
    closeLogin();
    openReset();
  };

  return {
    errors,
    watch,
    control,
    resetPopupOpen,
    loginPopupOpen,
    emailConfirmation,
    openLogin,
    closeLogin,
    closeReset,
    onResetClick,
    onSubmitHandler,
    showEmailConfirmation,
    hideEmailConfirmation,
  };
};

type SpanProps = JSX.IntrinsicElements["span"];

interface HelperLabelErrorMessageProps extends SpanProps {
  error?: FieldError;
}

export const HelperLabelErrorMessage = ({ error, ...rest }: HelperLabelErrorMessageProps) => {
  const { t } = useTranslation();

  if (!error?.message) {
    return null;
  }

  return <span {...rest}>{t(error.message)}</span>;
};

interface ErrorMessageProps extends Omit<MessageProps, "type" | "children" | "message"> {
  error?: FieldError;
}

export const ErrorMessage = ({ error, ...rest }: ErrorMessageProps) => {
  const { t } = useTranslation();

  if (!error?.message) {
    return null;
  }

  return (
    <Alert severity={"error"} {...rest}>
      {t(error.message)}
    </Alert>
  );
};

export const RegisterContactSalesSection = () => (
  <RegisterContactSalesRoot className={"scha__register-free__contact-sales"}>
    <Trans i18nKey={"page.registerFree.contactSales"}>
      Are you already a Schaeffler customer or sales partner? Please contact your Schaeffler sales representative for
      free access to{" "}
      <RegisterContactSalesLogo className={"scha__register-free__contact-sales__logo"}>medias</RegisterContactSalesLogo>{" "}
      <RegisterContactSalesLogoSuffix className={"scha__register-free__contact-sales__logo2"}>
        Business
      </RegisterContactSalesLogoSuffix>
      .
    </Trans>
  </RegisterContactSalesRoot>
);

const RegisterContactSalesRoot = styled("p")(({ theme }) => ({
  marginBottom: 0,
  marginTop: theme.spacing(1),
  [theme.breakpoints.up("desktop-s")]: {
    marginTop: theme.spacing(1.25),
  },
}));

const RegisterContactSalesLogo = styled("span")(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
}));

const RegisterContactSalesLogoSuffix = styled("span")(({ theme }) => ({
  color: theme.palette.text.disabled,
}));
